import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MessageSquare } from 'lucide-react';
import {
  CALL_STATUS_LABEL,
  CALL_STATUS_TONE,
  claimFullLabel,
  damageLabel,
  NO_SURVEY_LABEL,
  SURVEY_OUTCOME_LABEL,
  SURVEY_OUTCOME_TONE,
  SURVEY_OUTCOMES_WITH_CLAIM,
  type TrackerFdfcDetail,
} from '@tracker/shared';
import { trackerFdfcService } from '@/services/tracker-fdfc.service';
import { formatDateTime } from '@/lib/dateFormat';
import { getErrorMessage } from '@/lib/errors';
import { TONE_BADGE, TONE_TEXT } from '@/lib/tone';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CallStatusSelect } from './CallStatusSelect';
import { SurveyOutcomeSelect } from './SurveyOutcomeSelect';
import { DetailSection, SubHeading } from './DetailSection';

/** How many call-log entries show before "Show all". */
const CALL_LOG_PREVIEW = 6;

/**
 * The team's follow-up on one entry: what the person said about the kit, the
 * call status with a remark box, the call log, and every survey answer.
 */
export function FollowUpSection({ entry, canUpdate }: { entry: TrackerFdfcDetail; canUpdate: boolean }) {
  const queryClient = useQueryClient();
  const [remark, setRemark] = useState('');
  const [showAllCalls, setShowAllCalls] = useState(false);

  const addRemark = useMutation({
    // No status: the server logs the remark against the status as it stands and
    // leaves the stored one alone (sending the shown status would turn a derived
    // "No need to call" into a stored one, or undo a change made a moment ago).
    mutationFn: () => trackerFdfcService.logCall(entry.id, { remark }),
    onSuccess: () => {
      setRemark('');
      toast.success('Remark saved');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['tracker-fdfc'] }),
  });

  const calls = showAllCalls ? entry.call_logs : entry.call_logs.slice(0, CALL_LOG_PREVIEW);

  return (
    <DetailSection icon={MessageSquare} title="Follow-up">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-1.5 text-xs text-muted-foreground">Survey</div>
          <SurveyOutcomeSelect
            id={entry.id}
            outcome={entry.survey_outcome}
            damageAreas={entry.survey_damage_areas}
            damageDetail={entry.survey_damage_detail}
            damageClaim={entry.survey_damage_claim}
            chips
            disabled={!canUpdate}
          />
        </div>
        <div>
          <div className="mb-1.5 text-xs text-muted-foreground">Call</div>
          <div className="max-w-[13rem]">
            <CallStatusSelect id={entry.id} status={entry.call_status} disabled={!canUpdate} />
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            {entry.call_attempts > 0
              ? `${entry.call_attempts} ${entry.call_attempts === 1 ? 'call' : 'calls'} logged${
                  entry.last_called_at ? ` · last ${formatDateTime(entry.last_called_at)}` : ''
                }`
              : 'No calls logged yet'}
          </p>
        </div>
      </div>

      {canUpdate && (
        <form
          className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-start"
          onSubmit={(e) => {
            e.preventDefault();
            if (remark.trim()) addRemark.mutate();
          }}
        >
          <textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Add a remark…"
            aria-label="Call remark"
            maxLength={1000}
            rows={2}
            className="min-h-[2.5rem] w-full flex-1 rounded-md border border-input bg-background px-2.5 py-1.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <Button type="submit" size="sm" variant="outline" disabled={!remark.trim() || addRemark.isPending}>
            Save remark
          </Button>
        </form>
      )}

      <SubHeading>Call log</SubHeading>
      {entry.call_logs.length === 0 ? (
        <p className="text-xs text-muted-foreground">No calls logged yet.</p>
      ) : (
        <ol className="space-y-2">
          {calls.map((c) => (
            <li key={c.id} className="flex gap-2.5 text-sm">
              <span
                className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full bg-current', TONE_TEXT[CALL_STATUS_TONE[c.status]])}
                aria-hidden
              />
              <div className="min-w-0">
                <div>
                  <span className={cn('font-medium', TONE_TEXT[CALL_STATUS_TONE[c.status]])}>
                    {CALL_STATUS_LABEL[c.status]}
                  </span>
                  {c.remark && <span className="text-foreground"> — “{c.remark}”</span>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatDateTime(c.created_at)}
                  {c.called_by_name && ` · ${c.called_by_name}`}
                </div>
              </div>
            </li>
          ))}
          {entry.call_logs.length > CALL_LOG_PREVIEW && (
            <li>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setShowAllCalls((v) => !v)}
              >
                {showAllCalls ? 'Show fewer' : `Show all (${entry.call_logs.length})`}
              </button>
            </li>
          )}
        </ol>
      )}

      <SubHeading>Survey history</SubHeading>
      {entry.survey_responses.length === 0 ? (
        <p className="text-xs text-muted-foreground">{NO_SURVEY_LABEL}.</p>
      ) : (
        <ol className="space-y-2">
          {entry.survey_responses.map((s) => {
            const damage = damageLabel(s.damage_areas, s.damage_detail);
            const decision = claimFullLabel(s.damage_claim);
            return (
              <li key={s.id} className="text-sm">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant={TONE_BADGE[SURVEY_OUTCOME_TONE[s.outcome]]}>{SURVEY_OUTCOME_LABEL[s.outcome]}</Badge>
                  {damage && (
                    <span>
                      {s.outcome === 'not_received' ? 'send' : 'damaged'}: {damage}
                    </span>
                  )}
                  {decision && <span className="text-muted-foreground">· {decision}</span>}
                  {!decision && SURVEY_OUTCOMES_WITH_CLAIM.includes(s.outcome) && (
                    <span className={TONE_TEXT.warn}>· not decided yet</span>
                  )}
                </div>
                {s.notes && <p className="mt-1 whitespace-pre-wrap">{s.notes}</p>}
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {formatDateTime(s.submitted_at)}
                  {s.provider === 'form' ? ' · from the form' : s.created_by_name ? ` · ${s.created_by_name}` : ''}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </DetailSection>
  );
}
